import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withRouter, useHistory, Prompt } from 'react-router-dom';
import { connect } from 'react-redux';
import { Card, Container, Table } from 'react-bootstrap-5';

import ManagedInput from 'components/form/managed_input';

import { get as getLodash } from 'lodash';
import { get } from 'axios'; // Not using helpers/api because we need to retain snake casing on keys
import { put } from 'helpers/api';

import Option from './option_defaults/option';
import PageTemplateStyleSelector from './option_defaults/page_template_style_selector';
import SavePageModal from './shared/save_page_modal';
import { updatePageTemplateName } from './store/actions';
import { updatePageTemplateUserLabel } from './store/actions';
import PanelHeader from './shared/panel_header';
import { faTh, faWrench } from '@fortawesome/pro-solid-svg-icons';
import DefaultOptionMessage from './shared/default_option_message';
import Header from './shared/header';

export function PageTemplateForm({
  basePath,
  documentTemplateName,
  formDataDocTemplateName,
  match,
  templateType,
  updatePageTemplateName,
  updatePageTemplateUserLabel
}) {
  const [formData, setFormData] = useState({});
  const [pageTemplateName, setPageTemplateName] = useState(null);
  const [pageTemplateUserLabel, setPageTemplateUserLabel] = useState(null);
  const [options, setOptions] = useState([]);
  const [pageTemplateStyles, setPageTemplateStyles] = useState([]);
  const [docTemplates, setDocTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [dirty, setDirty] = useState(false);
  const { id } = match.params;
  const history = useHistory();
  const pathSegment = (() => {
    switch (templateType) {
    case 'Docs::UserPageTemplate': return `page_options/user_page_template/${id}`;
    case 'Docs::PdfPageTemplate':  return `page_options/pdf_page_template/${id}`;
    default:                       return `page_options/${id}`;
    }
  })();
  const path = `${basePath}/${pathSegment}`;
  const isPageTemplateTree = templateType === 'Docs::PageTemplateTree' || !templateType;

  React.useEffect(() => {
    get(path).then((response) => {
      const pageOptions = response.data.page_options || {};
      const isTree = templateType === 'Docs::PageTemplateTree' || !templateType;
      setFormData(
        isTree
          ? pageOptions
          : { ...pageOptions,
            page_name: getLodash(pageOptions, 'page_name', '') || response.data.page_template_name || '' }
      );
      setPageTemplateName(response.data.page_template_name);
      setPageTemplateUserLabel(response.data.page_template_user_label);
      setOptions(response.data.all_options);
      setPageTemplateStyles(response.data.page_template_styles);
      setDocTemplates(response.data.doc_templates);
      setLoading(false);
    });
  }, [id]);

  const handleFormUpdate = (newFormData) => {
    setFormData(newFormData);
    setDirty(true);
  };

  const handleBack = () => history.push('/');

  const updateName = (pageName) => {
    handleFormUpdate({ ...formData, page_name: pageName });
  };

  const updateUserLabel = (pageTemplateUserLabel) => {
    handleFormUpdate({ ...formData, page_template_user_label: pageTemplateUserLabel });
  };

  const updatePageTemplateStyle = (layout) => {
    handleFormUpdate({ ...formData, page_template_style_id: layout.identifier });
  };

  const removePageTemplateStyle = () => {
    handleFormUpdate({ ...formData, page_template_style_id: null });
  };

  const save = (unscope = false) => {
    setSaving(true);
    const { options } = formData;
    put(path, {
      unscope,
      pageDefaults: {
        pageName: formData.page_name,
        pageTemplateUserLabel: formData.page_template_user_label,
        options,
        pageTemplateStyleId: formData.page_template_style_id
      }
    }).then(() => {
      updatePageTemplateName(id, formData.page_name || pageTemplateName, templateType);
      if (isPageTemplateTree) {
        updatePageTemplateUserLabel(id, formData.page_template_user_label || pageTemplateUserLabel, templateType);
      }
      setDirty(false);
      setSaving(false);
      setShowSaveModal(false);
      setTimeout(handleBack, 300);
    });
  };

  const updateOption = (changes) => {
    handleFormUpdate({ ...formData, options: { ...formData.options, ...changes } });
  };

  const handleRemoveOptions = (names) => {
    const options = { ...formData.options };
    names.forEach((name) => {
      delete options[name];
    });

    handleFormUpdate({
      ...formData,
      options
    });
  };

  // options based on selected style_id
  const filteredOptions = useMemo(() => {
    const selectedStyleId = formData.page_template_style_id;
    return options.filter((option) => {
      return !option.style_id || !selectedStyleId || selectedStyleId === option.style_id;
    });
  }, [options, formData.page_template_style_id]);

  const pageNameRequired = !isPageTemplateTree;
  const pageNameInvalid = pageNameRequired && getLodash(formData, 'page_name', '').trim() === '';

  return (
    <div>
      <Header
        modelName="Page Template"
        saveDisabled={pageNameInvalid}
        saving={saving}
        templateName={getLodash(formData, 'page_name', '') || pageTemplateName || ''}
        onCancel={handleBack}
        onSave={() => setShowSaveModal(true)}
      />
      <Container className="my-8">
        <Card className="p-5">
          <Card.Body>
            <Prompt
              message="You have unsaved changes, are you sure you want to leave?"
              when={dirty}
            />

            <div className="mb-5">
              <h6 className="mb-4">Page Settings</h6>

              <div className="row">
                <div className="col-md-6">
                  <p className="fw-semibold my-2">
                    Page Name {pageNameRequired && <span className="text-danger">*</span>}
                  </p>
                  <ManagedInput
                    className="mb-3 form-control w-100"
                    disabled={loading}
                    name="page-name-input"
                    placeholder={pageTemplateName}
                    required={pageNameRequired}
                    value={getLodash(formData, 'page_name', '')}
                    onChange={change => updateName(change['page-name-input'])}
                  />
                </div>

                {isPageTemplateTree && (
                  <div className="col-md-6 mb-2">
                    <p className="fw-semibold my-2">Page Template Label</p>
                    <ManagedInput
                      className="mb-2 form-control w-100"
                      disabled={loading}
                      name="page-template-user-label-input"
                      placeholder={pageTemplateUserLabel}
                      value={getLodash(formData, 'page_template_user_label', '')}
                      onChange={change => updateUserLabel(change['page-template-user-label-input'])}
                    />
                    <small className="text-muted">
                      This label is for your organization. It will be visible when editing the document library.
                    </small>
                  </div>
                )}
              </div>

              {isPageTemplateTree && docTemplates.length > 0 && (
                <Table className="mb-5" size="sm">
                  <thead>
                    <tr>
                      <th>Used In</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docTemplates.map(({ path, name }) => (
                      <tr key={name}>
                        <td>
                          <a href={path} rel="noopener noreferrer" target="_blank">{name}</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              {isPageTemplateTree && (
                <>
                  <h6 className="mt-5">Default Behavior</h6>
                  <p className="my-3">
                    Below, you are able to set the default layout and page options for this page template.
                  </p>
                </>
              )}
            </div>

            {isPageTemplateTree && pageTemplateStyles.length > 0 && (
              <Card className="mb-5" id="page-layouts">
                <PanelHeader
                  icon={faTh}
                  subtitle="Choose which layout shows by default for this page"
                  title="Layout"
                />
                <Card.Body>
                  <DefaultOptionMessage/>
                  <PageTemplateStyleSelector
                    pageTemplateStyleId={formData.page_template_style_id}
                    pageTemplateStyles={pageTemplateStyles}
                    onRemove={removePageTemplateStyle}
                    onUpdate={updatePageTemplateStyle}
                  />
                </Card.Body>
              </Card>
            )}

            {isPageTemplateTree && filteredOptions.length > 0 && (
              <Card className="mb-5" id="page-options">
                <PanelHeader
                  icon={faWrench}
                  subtitle="Choose which options display by default for this page"
                  title="Page Options"
                />
                <Card.Body>
                  <DefaultOptionMessage/>
                  {filteredOptions.map((o, i) => (
                    <div className={i < filteredOptions.length - 1 ? 'mb-3' : ''} key={o.name}>
                      <Option
                        defaults={formData.options || {}}
                        option={o}
                        onRemove={handleRemoveOptions}
                        onUpdate={updateOption}
                      />
                    </div>
                  ))}
                </Card.Body>
              </Card>
            )}

            {!loading && (
              <SavePageModal
                pageName={formDataDocTemplateName || documentTemplateName}
                saving={saving}
                show={showSaveModal}
                onClose={() => setShowSaveModal(false)}
                onSave={save}
              />
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

PageTemplateForm.propTypes = {
  basePath: PropTypes.string.isRequired,
  documentTemplateName: PropTypes.string,
  formDataDocTemplateName: PropTypes.string,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  templateType: PropTypes.oneOf([
    'Docs::PageTemplateTree',
    'Docs::UserPageTemplate',
    'Docs::PdfPageTemplate'
  ]),
  updatePageTemplateName: PropTypes.func.isRequired,
  updatePageTemplateUserLabel: PropTypes.func
};

const mapStateToProps = ({ formData }) => ({ formDataDocTemplateName: formData.docName });
const mapDispatchToProps = { updatePageTemplateName, updatePageTemplateUserLabel };

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(PageTemplateForm));
